import { Box, HStack, Icon, Tag, Text, VStack } from "@chakra-ui/react"
import HoverableIconButton from "components/Atomos/Buttons/HoverableIconButton"
import DatePeriodFilter from "components/Atomos/Filter/Base/DatePeriodFilter"
import { DropdownFilter } from "components/Atomos/Filter/Base/DropdownFilter"
import { Td, Th, Tr } from "components/Moleculas/ResponsiveTable"
import TablePageTemplate from "components/Templates/TablePage"
import { Breadcrumb } from "layouts/LayoutDefault/Breadcrumb"
import { Cpf } from "models/domain/cpf.domain"
import ComplaintDetailsPage from "pages/Complaints/details"
import { useCallback, useEffect, useState } from "react"
import { FiEye } from "react-icons/fi"
import { useAppDispatch, useTypedSelector } from "store/hooks"
import { getComplaintsPaginatedRequest } from "store/modules/complaints/actions/getComplaintsPaged"
import { useQueryParam } from "utils/hooks/useQueryParam"
import { wait } from "utils/wait"

const PAGE_SIZE = 10 as const;

const ComplaintsPage = () => {
    const [search, setSearch] = useState('');
    const [, setShowDetailsComplaint] = useQueryParam<boolean>('showDetailsComplaint');
    const [status] = useQueryParam<string>('status');
    const [, setComplaintId] = useQueryParam<string>('complaintId');
    const [startDate, setStartDate] = useQueryParam<string>('start_date');
    const [endDate, setEndDate] = useQueryParam<string>('end_date');
    const [page, setPage] = useState(0);
    const dispatch = useAppDispatch();
    const fetchData = useCallback((
        index: number,
        pageSize: number,
        startDate?: string,
        endDate?: string,
        status?: string,
        search?: string
    ) => {
        dispatch(getComplaintsPaginatedRequest({
            index,
            pageSize,
            search,
            startDate,
            endDate,
            complaintStatus: status ? +status : undefined
        }))
    }, [dispatch]);

    useEffect(() => {
        fetchData(page, PAGE_SIZE, startDate, endDate, status, search);
    }, [fetchData, page, search, startDate, endDate, status]);    

    const {
        successPayload,
        isRequesting
    } = useTypedSelector(state => (
        state.complaints.getComplaintsPaged
    ));

    const openDetailsComplaintModal = () => {
        setShowDetailsComplaint(true);
    }

    return (
        <Box p={8}>
            <Breadcrumb paths={[{ name: "Início", link: '/' }, { name: 'Manifestações' }]} />
            <TablePageTemplate
                title="Manifestações"
                onSearch={setSearch}
                filters={(
                    <HStack>
                        <DropdownFilter
                            label="Status"
                            name="status"
                            options={[
                                { value: '1', name: 'Não atendido' },
                                { value: '2', name: 'Respondido pela prefeitura' },
                                { value: '3', name: 'Respondido pelo usuário' },
                                { value: '4', name: 'Encerrado' },
                            ]}
                        />
                        <DatePeriodFilter
                            onChange={(start, end) => {
                                setStartDate(start?.toLocaleDateString('pt-BR').split('/')?.reverse()?.join('-') ?? '');
                                setEndDate(end?.toLocaleDateString('pt-BR').split('/')?.reverse()?.join('-') ?? '');
                            }}
                        />
                    </HStack>
                )}
                tableHeader={(
                    <Tr>
                        <Th>Protocolo</Th>
                        <Th>Data de abertura</Th>
                        <Th>Quem abriu</Th>
                        <Th>Assunto</Th>
                        <Th>Status</Th>
                        <Th></Th>
                    </Tr>
                )}
                data={successPayload.items}
                renderTableItem={(item) => (
                    <Tr>
                        <Td>{item.protocolo}</Td>
                        <Td>
                            {new Date(`${item.dataAbertura}T03:00:00`).toLocaleDateString('pt-BR')}
                        </Td>
                        <Td>
                            <VStack align="flex-start">
                                <Text>{item.usuario}</Text>
                                <Text mt={0} color="gray.500" fontSize="small">
                                    {Cpf.fromString(item.cpfUsuario).getWithMask()}
                                </Text>
                            </VStack>
                        </Td>
                        <Td>
                            {item.titulo}
                        </Td>
                        <Td>
                            <Tag colorScheme={item.status === "Não atendida" ? "orange" : "blue"}>
                                {item.status}
                            </Tag>
                        </Td>
                        <HStack as={Td}>
                            <HoverableIconButton
                                label="Detalhes da manifestação"
                                icon={<Icon as={FiEye} />}
                                onClick={async () => {
                                    openDetailsComplaintModal();
                                    await wait(200);
                                    setComplaintId(item.idManifestacao)
                                }}
                            />
                        </HStack>
                    </Tr>
                )}
                onPageChange={setPage}
                pageProps={{
                    index: page,
                    pageSize: PAGE_SIZE,
                    totalItems: successPayload.totalCount,
                    hasNextPage: successPayload.hasNextPage,
                    hasPreviousPage: successPayload.hasPreviousPage,
                    isLoading: isRequesting
                }}
            />
            <ComplaintDetailsPage />
        </Box>
    )
}

export default ComplaintsPage